/**
 * migrateCookies.ts
 * One-time migration of legacy cookies to localStorage.
 * 
 * This migration runs once on app initialization and moves non-auth cookies
 * from document.cookie to localStorage for better security and control.
 */

import { storage } from "./storage";

/**
 * Legacy cookie names to migrate
 */
const LEGACY = [
  "welcomeModalVersion",
  "feedbackButtonDismissed",
  "userOnboardingTasks",
  "existing_account",
  "recent_issues",
];

/**
 * Read a cookie value by name
 */
function readCookie(n: string): string | null {
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + n.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Delete a cookie by setting its expiration to the past
 */
function kill(n: string): void {
  document.cookie = `${n}=; Max-Age=0; path=/`;
}

/**
 * Migrate legacy cookies to localStorage (runs once)
 * Safe to call multiple times - will only run once per client
 */
export function migrateLegacyCookiesOnce(): void {
  try {
    // Check if migration already completed
    if (storage.getJSON("migratedCookies", false)) return;

    for (const name of LEGACY) {
      const val = readCookie(name);
      if (!val) continue;

      if (name === "feedbackButtonDismissed") {
        // Store with 12-hour TTL
        storage.setJSONWithTTL(name, true, 12 * 60 * 60 * 1000);
      } else {
        // Parse JSON if possible, otherwise store as string
        const parsed = (() => {
          try {
            return JSON.parse(val);
          } catch {
            return val;
          }
        })();
        storage.setJSON(`cookie:${name}`, parsed);
      }

      // Remove the old cookie
      kill(name);
    }

    // Mark migration as complete
    storage.setJSON("migratedCookies", true);
  } catch {
    // Fail silently - migration is not critical
  }
}

